import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import React, { useEffect, useRef, useState } from 'react'
import * as style from 'src/app/CardFeedDetails/components/CardStatusFilter.style'
import SvgFilterFunnel from 'src/components/Svgs/SvgFilterFunnel'
import { useTranslate } from 'src/localization/Trns'
import { CardFeedCardStatusFilter } from 'src/queries/cardFeeds/cardFeeds.models'

interface CardStatusFilterProps {
  statuses: CardFeedCardStatusFilter[]
  onChange: (next: CardFeedCardStatusFilter[]) => void
}

const STATUS_OPTIONS: Array<{
  value: CardFeedCardStatusFilter
  label: string
}> = [
  { value: 'UNASSIGNED', label: 'Unassigned' },
  { value: 'PENDING_INVITE_SENT', label: 'Invite Sent' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Inactive' },
  { value: 'ASSIGNED', label: 'Assigned' },
]

const CardStatusFilter: React.FC<CardStatusFilterProps> = ({
  statuses,
  onChange,
}) => {
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const prevOpenRef = useRef(open)

  // Return focus to the trigger when the popover closes (Escape, outside-click,
  // or selection) so keyboard users aren't dropped at the top of the document.
  // Skip the initial mount (open starts false) by only acting on a true→false
  // transition.
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      triggerRef.current?.focus()
    }
    prevOpenRef.current = open
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const toggle = (value: CardFeedCardStatusFilter): void => {
    onChange(
      statuses.includes(value)
        ? statuses.filter((s) => s !== value)
        : [...statuses, value],
    )
  }

  return (
    <div css={style.container} ref={containerRef}>
      <Button
        ref={triggerRef}
        variant="toolbar"
        dropShadow={false}
        onClick={(): void => setOpen((prev) => !prev)}
        data-testid="card-feed-cards-filter"
        aria-label={translate('Toggle filters')}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls="card-feed-status-filter-popover"
      >
        <SvgFilterFunnel />
        {statuses.length > 0 && (
          <span
            css={style.countBadge}
            data-testid="card-feed-cards-filter-count"
          >
            {statuses.length}
          </span>
        )}
      </Button>
      {open && (
        <div
          id="card-feed-status-filter-popover"
          role="group"
          aria-label={translate('Filter by status')}
          css={style.popover}
          data-testid="card-feed-cards-filter-popover"
        >
          {STATUS_OPTIONS.map(({ value, label }) => (
            <label key={value} css={style.option}>
              <Checkbox
                checked={statuses.includes(value)}
                onChange={(): void => toggle(value)}
                aria-label={translate(label)}
                data-testid={`card-feed-status-filter-${value}`}
              />
              {translate(label)}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default CardStatusFilter
